import React from 'react'
import { Button, Grid, Typography } from '@mui/material'
import { Navigate, NavLink } from 'react-router-dom'
import { object, string } from 'yup'

import { FadeInWrapper, PageTitle } from '../components'
import {
  Form,
  FormSchema,
  FormSchemaMessages,
  SubmitButton,
  SubmitErrorMessage,
  TextFieldControl,
} from '../components/form'
import { ChangePasswordParams, useChangePasswordMutation } from '../services'
import { APP_ROUTES } from '../constants.ts'
import { useLogin } from '@/stores/slices/user-slice.ts'

const changePasswordFormSchema = object<FormSchema<ChangePasswordParams>>({
  login: string().required(FormSchemaMessages.required),
  currentPassword: string().required(FormSchemaMessages.required),
  newPassword: string().required(FormSchemaMessages.required).min(8, FormSchemaMessages.minLength(8)),
  confirmPassword: string()
    .required(FormSchemaMessages.required)
    .min(8, FormSchemaMessages.minLength(8))
    .when(['newPassword'], ([newPassword], schema) =>
      schema.test({
        name: 'isEqualPassword',
        exclusive: true,
        test: (value: string) => value === newPassword,
        message: 'Пароли не совпадают',
      })
    ),
})

export const ChangePasswordPage = (): React.ReactElement => {
  const currentLogin = useLogin()
  const { data, isError, error, mutateAsync, isSuccess } = useChangePasswordMutation()

  const handlePasswordChange = async (formValues: ChangePasswordParams) => mutateAsync(formValues)

  if (!currentLogin) {
    return <Navigate to={APP_ROUTES.Login} />
  }

  if (isSuccess) {
    return (
      <FadeInWrapper>
        <Typography variant="h2" sx={{ mb: 3 }} textAlign="center">
          {data?.data.message}
        </Typography>
        <Button component={NavLink} to={APP_ROUTES.Login} fullWidth>
          Войти в систему
        </Button>
      </FadeInWrapper>
    )
  }

  return (
    <FadeInWrapper>
      <PageTitle
        title="Смена пароля"
        description={`Вам необходимо сменить пароль для учетной записи ${currentLogin}`}
      />
      <Form<ChangePasswordParams>
        name="changePassword"
        validationSchema={changePasswordFormSchema}
        initialValues={{ login: currentLogin }}
        onSubmit={handlePasswordChange}
      >
        <Grid container spacing={2} flexDirection="column">
          <Grid item xs={12}>
            <TextFieldControl<ChangePasswordParams>
              name="currentPassword"
              fullWidth
              label="Временный пароль"
              type="password"
            />
          </Grid>
          <Grid item xs={12}>
            <TextFieldControl<ChangePasswordParams>
              name="newPassword"
              fullWidth
              label="Новый пароль"
              type="password"
              revalidateFields={['confirmPassword']}
            />
          </Grid>
          <Grid item xs={12}>
            <TextFieldControl<ChangePasswordParams>
              name="confirmPassword"
              fullWidth
              label="Подтверждение пароля"
              type="password"
            />
          </Grid>
          <Grid item xs={12}>
            <SubmitErrorMessage isError={isError} message={error?.response?.data.message} />
            <SubmitButton>Сменить пароль</SubmitButton>
          </Grid>
        </Grid>
      </Form>
    </FadeInWrapper>
  )
}
